import { z } from 'zod';

/**
 * Identifiers for the five Rynora React surfaces. Used by the per-app
 * theme system (see packages/theme/src/useAppTheme.ts) and by the
 * admin Theme settings page.
 *
 * Adding a new app means adding it here AND in packages/theme. The
 * single-source rule is: this file owns the list, packages/theme
 * imports from here.
 */
export const APP_IDS = ['landing-page', 'flow-builder', 'admin-console', 'admin-logs', 'admin-settings'] as const;
export type AppId = (typeof APP_IDS)[number];
export const AppIdSchema = z.enum(APP_IDS);

export const THEME_MODES = ['system', 'light', 'dark'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];
export const ThemeModeSchema = z.enum(THEME_MODES);

export const AppThemeMapSchema = z.object({
  'landing-page': ThemeModeSchema,
  'flow-builder': ThemeModeSchema,
  'admin-console': ThemeModeSchema,
  'admin-logs': ThemeModeSchema,
  'admin-settings': ThemeModeSchema,
});
export type AppThemeMap = z.infer<typeof AppThemeMapSchema>;

export const DEFAULT_APP_THEMES: AppThemeMap = {
  'landing-page': 'system',
  'flow-builder': 'system',
  'admin-console': 'system',
  'admin-logs': 'system',
  'admin-settings': 'system',
};

/**
 * GET /api/theme/apps response.
 */
export const AppsThemeResponse = z.object({
  apps: AppThemeMapSchema,
  updatedAt: z.string().nullable(),
});
export type AppsThemeResponse = z.infer<typeof AppsThemeResponse>;

/**
 * Brand-palette block (per light/dark) — kept loose because the browser
 * is the canonical CSS validator. Length-bound to prevent garbage.
 */
const cssValue = z.string().min(1).max(64);
export const AccentBlockSchema = z.object({
  accent: cssValue,
  accentStrong: cssValue,
  accentLight: cssValue,
  accentGlow: cssValue,
  gradientStart: cssValue,
  gradientEnd: cssValue,
});
export type AccentBlock = z.infer<typeof AccentBlockSchema>;
